/**
 * Custom editor colors (Slice 3) — a user-authored token-color table that
 * sits alongside the built-in presets in `monacoColorSchemes.ts`. Unlike a
 * preset, custom colors carry no WCAG floor (that is the user's own choice);
 * the Settings panel surfaces a live contrast warning instead of a hard gate.
 *
 * Seeded from a preset (`seedCustomEditorColors`), edited per-token via the
 * Settings → UI → Editor colors → Custom panel, and guarded on the way back
 * in from storage/imports (`normalizeCustomEditorColors`) exactly like
 * `normalizeEditorColorScheme` guards the preset id.
 */
import {
  getEditorColorScheme,
  getEditorColorSchemeSwatches,
  type EditorColorSchemeId,
  type EditorColorSchemeTokenColors,
  type EditorColorSelection,
} from './monacoColorSchemes';
// `monacoTheme.ts` imports `CustomEditorColors` back from this module as a
// TYPE-ONLY `import type`, which esbuild/tsc always elide — so this value
// import creates no runtime circular dependency between the two files.
import { JAM_MONACO_DARK, JAM_MONACO_LIGHT } from './monacoTheme';

export type CustomEditorColors = {
  light: EditorColorSchemeTokenColors;
  dark: EditorColorSchemeTokenColors;
};

const TOKEN_KEYS: Array<keyof EditorColorSchemeTokenColors> = [
  'keyword',
  'identifier',
  'number',
  'string',
  'string.double',
  'operator',
  'comment',
  'comment.quote',
  'predefined',
];

const HEX_RE = /^#[0-9a-f]{6}$/i;

/** Deep-copies both variants of `fromScheme` — never aliases the preset's own objects. */
export function seedCustomEditorColors(fromScheme: EditorColorSchemeId): CustomEditorColors {
  // `getEditorColorScheme` already falls back to the first (`default`) entry
  // for an id it doesn't recognize, so a non-preset id seeds from 'default'.
  const scheme = getEditorColorScheme(fromScheme);
  return {
    light: { ...scheme.light },
    dark: { ...scheme.dark },
  };
}

function normalizeVariant(
  variant: unknown,
  fallback: EditorColorSchemeTokenColors,
): EditorColorSchemeTokenColors {
  const source = variant && typeof variant === 'object' ? (variant as Record<string, unknown>) : {};
  const result = {} as EditorColorSchemeTokenColors;
  for (const token of TOKEN_KEYS) {
    const raw = source[token];
    result[token] = typeof raw === 'string' && HEX_RE.test(raw) ? raw : fallback[token];
  }
  return result;
}

/**
 * Guards settings-bundle imports and hand-edited storage: `null` unless
 * `value` is an object. Otherwise, per token+mode, an invalid or missing leaf
 * falls back to the `default` preset's value for that token — the same
 * per-leaf-repair shape `normalizeEditorColorScheme` uses at the scheme-id
 * level.
 */
export function normalizeCustomEditorColors(value: unknown): CustomEditorColors | null {
  if (!value || typeof value !== 'object') return null;
  const source = value as { light?: unknown; dark?: unknown };
  const defaultScheme = getEditorColorScheme('default');
  return {
    light: normalizeVariant(source.light, defaultScheme.light),
    dark: normalizeVariant(source.dark, defaultScheme.dark),
  };
}

/**
 * Resolves the light+dark token tables to render for `selection` — a
 * preset's own tables, or `customColors` (falling back to the `default`
 * preset when `selection` is 'custom' with no table yet). Used by the
 * Editor colors dialog's preview panes, which need to resolve an ARBITRARY
 * selection (including a hover-previewed one, not just "the applied one") —
 * `monacoTheme.ts` keeps its own private version of this same resolution
 * (driven by its module state) rather than importing this one, so that its
 * `import type { CustomEditorColors }` from this file stays type-only and
 * the two files don't form a real runtime cycle (see that file's header).
 */
export function resolveEditorColorTables(
  selection: EditorColorSelection,
  customColors: CustomEditorColors | null,
): CustomEditorColors {
  if (selection === 'custom') {
    if (customColors) return customColors;
    const fallback = getEditorColorScheme('default');
    return { light: fallback.light, dark: fallback.dark };
  }
  const scheme = getEditorColorScheme(selection);
  return { light: scheme.light, dark: scheme.dark };
}

/**
 * The 4 preview swatches (keyword, string, number, comment) for `selection`
 * in the current app mode. Presets delegate to `getEditorColorSchemeSwatches`;
 * 'custom' reads the user's own table (falling back to the `default` preset
 * when null) — the one place that fallback rule is written, shared by the
 * Settings row's trigger button and the dialog's scheme list.
 */
export function getSwatchesForSelection(
  selection: EditorColorSelection,
  isDark: boolean,
  customColors: CustomEditorColors | null,
): string[] {
  if (selection === 'custom') {
    const tables = customColors ?? seedCustomEditorColors('default');
    const variant = isDark ? tables.dark : tables.light;
    return [variant.keyword, variant.string, variant.number, variant.comment];
  }
  return getEditorColorSchemeSwatches(selection, isDark);
}

export type CustomColorPickerId =
  | 'keyword'
  | 'predefined'
  | 'string'
  | 'number'
  | 'identifier'
  | 'operator'
  | 'comment';

export interface CustomColorPickerRow {
  id: CustomColorPickerId;
  label: string;
  /** Editing this row writes every token here; the row displays the first token's value. */
  tokens: readonly (keyof EditorColorSchemeTokenColors)[];
}

/** UI grouping for the Custom panel's picker rows — covers all 9 token classes exactly once. */
export const CUSTOM_COLOR_PICKERS: readonly CustomColorPickerRow[] = [
  { id: 'keyword', label: 'Keywords', tokens: ['keyword'] },
  { id: 'predefined', label: 'Functions', tokens: ['predefined'] },
  { id: 'string', label: 'Strings', tokens: ['string', 'string.double'] },
  { id: 'number', label: 'Numbers', tokens: ['number'] },
  { id: 'identifier', label: 'Identifiers', tokens: ['identifier'] },
  { id: 'operator', label: 'Operators', tokens: ['operator'] },
  { id: 'comment', label: 'Comments', tokens: ['comment', 'comment.quote'] },
];

/** One picker row's value changed, for one variant, between a draft and its snapshot. */
export interface CustomColorPickerChange {
  token: CustomColorPickerId;
  mode: 'light' | 'dark';
}

/**
 * Per-picker-row × variant diff between a draft custom color table and its
 * pre-dialog snapshot — comparing at each row's first token, same as what
 * `CustomColorPickerRow` displays and edits (a row always writes every one
 * of its tokens to the same hex, so the first token alone is a faithful
 * proxy for "did this row change"). Used by the Editor colors dialog's
 * Confirm handler to fire `feature:editor_custom_color_set` once per
 * changed row.
 */
export function diffCustomColorPickerChanges(
  draft: CustomEditorColors | null,
  snapshot: CustomEditorColors | null,
): CustomColorPickerChange[] {
  if (!draft) return [];
  const changes: CustomColorPickerChange[] = [];
  for (const mode of ['light', 'dark'] as const) {
    for (const picker of CUSTOM_COLOR_PICKERS) {
      const token = picker.tokens[0];
      if (draft[mode][token] !== snapshot?.[mode]?.[token]) {
        changes.push({ token: picker.id, mode });
      }
    }
  }
  return changes;
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as [number, number, number];
}

function srgbChannelToLinear(c: number): number {
  const cs = c / 255;
  return cs <= 0.04045 ? cs / 12.92 : Math.pow((cs + 0.055) / 1.055, 2.4);
}

/** WCAG 2.x relative luminance. */
function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map(srgbChannelToLinear);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG 2.x contrast ratio, always >= 1. */
function contrastRatio(hexA: string, hexB: string): number {
  const lA = relativeLuminance(hexA);
  const lB = relativeLuminance(hexB);
  const lighter = Math.max(lA, lB);
  const darker = Math.min(lA, lB);
  return (lighter + 0.05) / (darker + 0.05);
}

/** WCAG 2.x contrast ratio of `hex` against the actual editor canvas color for `isDark`. */
export function contrastAgainstEditorBackground(hex: string, isDark: boolean): number {
  const bg = isDark
    ? JAM_MONACO_DARK.colors['editor.background']
    : JAM_MONACO_LIGHT.colors['editor.background'];
  return contrastRatio(hex, bg);
}
